/**
 * An extension of the [View]{@link module:core/view} module, with functionality relating to input handling and other form-related actions.
 *
 * @module core/form
 */

import * as View from './view';
import * as Analytics from './analytics';
import * as Util from './util';

/**
 * Default form configuration.
 *
 * @since 3.2.7
 *
 * @type {object}
 *
 * @example
 * {
 *   repeater: {
 *     max_blocks: 10,
 *   }
 * }
 */
export const config = {
  repeater: {
    max_blocks: 10,
  },
};

/**
 * This object holds form action functions.
 *
 * Form action functions can be created to run in-app functionality on form submissions. They can be added as properties of this object (e.g. for custom [project]{@link module:project/customize} functionality), and the names of which should correspond to form action names in [form content]{@link module:core/config._content_type_form}.
 *
 * Form action functions are run during form [`submit`]{@link module:core/form.submit}.
 *
 * By default, this object is empty, with the exception of [offline]{@link module:core/offline} builds, which add password close functionality.
 *
 * @type {object}
 *
 * @since 3.0.0
 *
 * @see Used by:
 * @see [**`Offline.init`**]{@link module:core/offline.init}
 * @see [**`submit`**]{@link module:core/form.submit}
 *
 * @example
 * // add password close functionality
 * actions.passwordClose = (form) => {
 *   // do things, then return true if form is valid
 *   return true;
 * };
 *
 * @example
 * <!-- form with password close action -->
 * <div class="js-form" data-action="passwordClose">
 *   <input type="password">
 *   <button type="submit">Submit</button>
 * </div>
 */
export const actions = {};

/**
 * Set all input fields to their initial (unselected) states. Runs each input type's `init` method.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`button.init`]{@link module:core/form.button.init}
 * @see [`radio.init`]{@link module:core/form.radio.init}
 * @see [`checkbox.init`]{@link module:core/form.checkbox.init}
 * @see [`select.init`]{@link module:core/form.select.init}
 * @see Used by:
 * @see [**`View.postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 * @see [**`View.facet.reset`**]{@link module:core/view.facet.reset}
 */
export const inputInit = () => {
  button.init();
  radio.init();
  checkbox.init();
  select.init();
  repeater.init();
}

/**
 * Disable inputs by setting `aria-disabled` and `disabled` attributes on specified elements. If no inputs are specified, all inputs in the current [`container`]{@link module:core/view.container} will be disabled.
 *
 * If the provided inputs contain any active selections, they will be deselected and the app state updated before disabled.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} [inputs] - Input(s) to disable. Default: `View.container.find('.input')`
 *
 * @param {boolean} [hide] - When enabled, inputs will also be hidden, which includes adding the class `hidden` and attribute `aria-hidden="true"` to the containing `<li>` tag. Default: `false`
 *
 * @see [`deselectInputs`]{@link module:core/form.deselectInputs}
 * @see [`View.updateState`]{@link module:core/view.updateState}
 * @see Used by:
 * @see [**`facet.removeUnavailable`**]{@link module:core/view.facet.removeUnavailable}
 * @see [**`facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
 */
export const disableInputs = (inputs = View.container.find('.input'), hide = false) => {
  // passively deselect any active elements within selection, then update app state
  let active = inputs.filter('.active');
  if (active.length) {
    deselectInputs(active, true);
    View.updateState();
  }

  // disable inputs
  inputs.attr({
    'aria-disabled': true,
    'disabled': '',
  });

  // hide inputs, if enabled
  if (hide) inputs.closest('li').addClass('hidden').attr('aria-hidden', 'true');
}

/**
 * Enable inputs by removing `aria-disabled` and `disabled` attributes from specified elements. If no inputs are specified, all inputs will be enabled.
 *
 * *Note this function does not "select" or activate inputs, that should be done with [`View.inputActions`]{@link module:core/view.inputActions}.*
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} [inputs] - jQuery object containing inputs to enable. Default: `View.container.find('.input')`
 *
 * @param {boolean} [show] - When enabled, inputs will also be shown, which includes removing the class `hidden` and setting attribute `aria-hidden="false"` to the containing `<li>` tag. Default: `false`
 *
 * @see Used by:
 * @see [**`View.facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
 */
export const enableInputs = (inputs = View.container.find('.input'), show = false) => {
  // remove disable attributes
  inputs.removeAttr('aria-disabled disabled');
  // show inputs, if enabled
  if (show) inputs.closest('li').removeClass('hidden').attr('aria-hidden', 'false');
}

/**
 * Deselect input field(s). Runs [`View.inputActions`]{@link module:core/view.inputActions} with `deselect` param enabled on provided inputs.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} inputs - jQuery object representing input(s) to be deselected
 * @param {boolean} [passive] - Enable or disable passive deselection. If enabled, [view state updates]{@link module:core/view.updateState} may need to be run as well. See [`View.inputActions`]{@link module:core/view.inputActions} for more info the passive parameter.
 *
 * @see [`View.inputActions`]{@link module:core/view.inputActions}
 */
export const deselectInputs = (inputs, passive = false) => {
  inputs.each((i, element) => {
    View.inputActions($(element), true, passive);
  });
}

/**
 * Show or hide the "clear field" button (`.js-clearField`) on single-choice fields (radio or select). The button is shown if active inputs are present, and hidden if not.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @param {jQuery} field - Field object.
 *
 * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
 * @see [**`facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
 */
export const setClearFieldState = (field) => {
  let fieldType = field.attr('data-field-type');
  // do nothing if not radio or select fields
  if (fieldType !== 'radio' && fieldType !== 'select') return;
  // determine if field is active
  let fieldActive = !!View.getActiveInputs(field).length;
  // set clear field button state accordingly
  field.find('.js-clearField').attr('aria-hidden', !fieldActive);
};

/**
 * Remove form fields that contain no inputs.
 *
 * Empty form fields may be a result of several processes, including removal due to global conditionals during [current content preparation]{@link module:core/app~transformContent}, or during [facet filtering]{@link module:core/view.facet.filter} when an input value is not found in product inventory.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} [target] - jQuery object of a target element to check for empty fields. Default: [`View.container`]{@link module:core/view.container}
 *
 * @see Used by:
 * @see [**`View.removeUnusedElements`**]{@link module:core/view~removeUnusedElements}
 */
export const removeEmptyFields = (target = View.container) => {
  target.find('.js-field').each((i, element) => {
    const field = $(element);
    const inputs = field.find('.input, input, .js-submit');
    if (inputs.length === 0) {
      field.remove();
    }
  });
}

/**
 * Set form submit button state.
 *
 * For each form content type that contains a [submit]{@link module:core/config._field_type_submit} field, determine if submission requirements are met, and then set the submit button state accordingly.
 *
 * @method
 *
 * @since 3.2.3
 *
 * @see [`requiredFields`]{@link module:core/view.requiredFields}
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 */
export const setSubmitState = () => {
  // find submit inputs
  const submits = View.container.find('[data-field-type="submit"]');
  // if no submits, stop here
  if (!submits.length) return;
  // determine state of each submit
  submits.each(function(i, e) {
    const submit = $(e).find('.js-submit');
    const requireAll = submit.is('[data-require-all-inputs]');
    const inputContainer = requireAll ? View.container : submit.closest('.js-form');
    // set default submit state
    let allowSubmit = true;
    // if there are any inactive required fields, fail
    if (View.requiredFields(inputContainer).length) allowSubmit = false;
    // set submit state
    submit.attr('aria-disabled', !allowSubmit).prop('disabled', !allowSubmit);
  });
};

/**
 * Run form action and error handling.
 *
 * Action functions are defined in [`actions`]{@link module:core/form.actions}. Functions are passed the current form jQuery element as a parameter, must include any form validation if necessary. If a form needs validation, the action function should return a boolean `false` if validation fails, and `true` or no return value if valid.
 *
 * Functions that fail validation will display any error messages included in [form content type settings]{@link module:core/config._content_type_form}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} form - Form to submit.
 *
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
 * @see [**`text.addEventListeners`**]{@link module:core/form.text.addEventListeners}
 */
export const submit = (form) => {
  // flush debounced text actions
  View.config.debounce.textInput.flush();
  // clear old errors
  form.find('.js-formError').removeClass('active');
  // check HTML5 input validity. if any input fails, stop here.
  let inputArray = form.find('input').toArray();
  let invalidInputs = false;
  for (let input of inputArray) {
    if (!input.checkValidity()) {
      input.reportValidity();
      invalidInputs = true;
    }
  }
  if (invalidInputs) return;
  // get form action
  let action = form.attr('data-action');
  // check if this action is defined
  if (actions.hasOwnProperty(action)) {
    // run action function
    let formValid = actions[action](form);
    if (!formValid) {
      // add slight delay so new submission attempts will show error animation
      setTimeout(() => {
        form.find('.js-formError').addClass('active');
      }, 1);
    }
  }
}

/**
 * Add general form-related event listeners to app [`container`]{@link module:core/view.container}.
 *
 * Event listeners added in this method include:
 * - [Form submit]{@link module:core/form.submit} on "submit" type button click.
 * - [Clear field]{@link module:core/form.deselectInputs} and [set "clear field" button state]{@link module:core/form.setClearFieldState} on `.js-clearField` click.
 *
 * In addition, event listener functions for specific form interactions are also run, including:
 *
 * - [`text`]{@link module:core/form.text.addEventListeners}
 * - [`button`]{@link module:core/form.button.addEventListeners}
 * - [`radio`]{@link module:core/form.radio.addEventListeners}
 * - [`checkbox`]{@link module:core/form.checkbox.addEventListeners}
 * - [`select`]{@link module:core/form.select.addEventListeners}
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`submit`]{@link module:core/form.submit}
 * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
 * @see [`deselectInputs`]{@link module:core/form.deselectInputs}
 * @see [`setClearFieldState`]{@link module:core/form.setClearFieldState}
 * @see [`text.addEventListeners`]{@link module:core/form.text.addEventListeners}
 * @see [`button.addEventListeners`]{@link module:core/form.button.addEventListeners}
 * @see [`radio.addEventListeners`]{@link module:core/form.radio.addEventListeners}
 * @see [`checkbox.addEventListeners`]{@link module:core/form.checkbox.addEventListeners}
 * @see [`select.addEventListeners`]{@link module:core/form.select.addEventListeners}
 * @see [`file.addEventListeners`]{@link module:core/form.file.addEventListeners}
 * @see Used by:
 * @see [**`View.addEventListeners`**]{@link module:core/view~addEventListeners}
 */
export const addEventListeners = () => {
  // submit form - click
  View.container.on('click', '.js-submit', (event) => {
    let form = $(event.currentTarget).closest('.js-form');
    submit(form);
  });

  // clear field
  View.container.on('click', '.js-clearField', (event) => {
    const field = $(event.currentTarget).closest('.js-field');
    const activeInput = View.getActiveInputs(field);
    // deselect active input
    deselectInputs(activeInput);
    setClearFieldState(field);
    // set focus on first focusable item in field
    field.find(':focusable').not('.js-clearField').first().focus();
  });

  // add individual element listeners
  text.addEventListeners();
  button.addEventListeners();
  radio.addEventListeners();
  checkbox.addEventListeners();
  select.addEventListeners();
  file.addEventListeners();
  repeater.addEventListeners();
}

/**
 * Manage text input elements. See content settings for [text]{@link module:core/config._field_type_text} and [password]{@link module:core/config._field_type_password} field types for usage information.
 *
 * @namespace
 *
 * @since 3.2.3
 *
 * @type {object}
 */
export const text = {
  /**
   * Event listeners for text input interactions.
   *
   * - [Run input actions]{@link module:core/view.inputActions} on input in `.js-textInput`. *This action is debounced by 300ms.*
   * - Flush debounced input actions on keydown within `.js-textInput`, if key code is *enter* or *tab* key.
   * - [Form submit]{@link module:core/form.submit} on keydown within `.js-textInput`, if key code is *enter* key.
   *
   * @memberOf module:core/form.text
   *
   * @since 3.2.3
   *
   * @see [`submit`]{@link module:core/form.submit}
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    // debounce input actions
    View.config.debounce.textInput = Util.debounce((event) => {
      View.inputActions($(event.currentTarget));
    }, 300);

    // keydown text field updates
    View.container.on('keydown', '.js-textInput', (event) => {
      // flush debounce on tab
      if (event.keyCode === View.keycode.tab) View.config.debounce.textInput.flush();
      // stop here if not enter keypress
      if (event.keyCode !== View.keycode.enter) return;
      // submit form
      submit($(event.currentTarget).parents('.js-form'));
    });

    // text field updates on input update
    View.container.on('input', '.js-textInput', (event) => {
      View.config.debounce.textInput(event);
    });
  },

  /**
   * Set text input state. Input is considered "active" when it contains a value.
   *
   * In addition to setting the active state, if the input has any attributes associated with it, the `label` property of those attributes will be updated with the current text value.
   *
   * @memberOf module:core/form.text
   *
   * @since 3.2.3
   *
   * @param {jQuery} input - Text input object.
   *
   * @see [`View.getElementData`]{@link module:core/view.getElementData}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input) => {
    // set active state
    const value = input.val();
    const active = !!value.length;
    input.toggleClass('active', active);
    // update attributes values
    const inputData = View.getElementData(input[0]);
    // if no attributes are present, stop here
    if (!inputData.attributes) return;
    // update each attribute label
    for (let attribute of inputData.attributes) attribute.label = value;
  },
};

/**
 * Manage button input elements. See content settings for [button field types]{@link module:core/config._field_type_button} for usage information.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const button = {
  /**
   * Event listeners for button interaction.
   *
   * - Runs [input actions]{@link module:core/view.inputActions} on `.js-buttonOption` click
   *
   * @memberOf module:core/form.button
   *
   * @since 3.0.0
   *
   * @see [`Analytics.event`]{@link module:core/analytics.event}
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    View.container.on('click', '.js-buttonOption', (event) => {
      View.inputActions($(event.currentTarget));
    });
  },

  /**
   * Initialize button inputs to default (unselected) state. For each input, removes attributes `aria-pressed`, `aria-disabled`, and `disabled`, and removes class `active`.
   *
   * @memberOf module:core/form.button
   *
   * @since 3.0.0
   *
   * @param {jQuery} [inputs] - Buttons inputs to initialize. Default: `View.container.find('.js-buttonOption')`
   *
   * @see Used by:
   * @see [**`button.setState`**]{@link module:core/form.button.setState}
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  init: (inputs = View.container.find('.js-buttonOption')) => {
    inputs.removeClass('active').removeAttr('aria-pressed aria-disabled disabled');
  },

  /**
   * Set button state.
   *
   * If running an external app, no action will be taken here, as that functionality is handled in [`Offline.runApp`]{@link module:core/offline~runApp}.
   *
   * If a button is not "active", it will be activated, by adding `active` class and setting `aria-pressed: true`. Additionally, the [analytics input event]{@link module:core/analytics.inputEvent} is run.
   *
   * If active or using `deselect` parameter, button state will be reset with [`init`]{@link module:core/form.button.init}.
   *
   * @memberOf module:core/form.button
   *
   * @since 3.0.0
   *
   * @param {jQuery} input - Button object.
   * @param {boolean} [deselect] - Force input deselection. Default: `false`
   * @param {boolean} [passive] - Whether input is being selected passively. Default: `false`
   *
   * @see [`button.init`]{@link module:core/form.button.init}
   * @see [`Analytics.inputEvent`]{@link module:core/analytics.inputEvent}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input, deselect = false, passive = false) => {
    // do nothing if running an app
    if (input.is('[data-run-app]')) return;

    const buttonActive = input.hasClass('active');

    // activate button
    if (!buttonActive) {
      input.addClass('active').attr('aria-pressed', 'true');
      // run input analytics
      Analytics.inputEvent(input, deselect, passive);
    }

    // deactivate button if multi select (or forced deselect)
    if (buttonActive || deselect) {
      button.init(input);
    }
  },
}

/**
 * Manage radio input elements. See content settings for [radio field types]{@link module:core/config._field_type_radio} for usage information.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const radio = {
  /**
   * Event listeners for radio input interaction.
   *
   * - Runs [input actions]{@link module:core/view.inputActions} on `.js-radioOption` input state change
   *
   * @memberOf module:core/form.radio
   *
   * @since 3.0.0
   *
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    // radio interaction
    View.container.on('change', '.js-radioOption', (event) => {
      View.inputActions($(event.currentTarget));
    });
  },

  /**
   * Initializes radio inputs to default (unselected) state.
   *
   * For each input, sets property `checked: false`, removes attributes `aria-disabled` and `disabled`, and removes class `active`.
   *
   * @memberOf module:core/form.radio
   *
   * @since 3.0.0
   *
   * @param {jQuery} [inputs] - Radio inputs to initialize. Default: `View.container.find('.js-radioOption')`
   *
   * @see Used by:
   * @see [**`radio.setState`**]{@link module:core/form.radio.setState}
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  init: (inputs = View.container.find('.js-radioOption')) => {
    // uncheck all radio inputs
    inputs.prop('checked', false).removeAttr('aria-disabled disabled').removeClass('active');
  },

  /**
   * Manage radio states.
   *
   * If an option is active and the `deselect` param is enabled, the option will be reset with [`init`]{@link module:core/form.radio.init}.
   *
   * If option is neither active nor being deselected:
   * - The input will be activated by setting the property `checked: true` and adding class `active`.
   * - Sibling options within the same group have their `active` class removed.
   * - The [analytics input event]{@link module:core/analytics.inputEvent} is run.
   *
   * *Note that in most cases, setting the `checked` property here is not strictly required, as it will have already been set by the user interaction. However, in some cases an input is not selected by direct interaction, e.g. when setting active selections on view load, so the property is set here as well to ensure the input is checked.*
   *
   * @memberOf module:core/form.radio
   *
   * @since 3.0.0
   *
   * @param {jQuery} input - Radio input object.
   * @param {boolean} [deselect] - Force input deselection. Default: `false`
   * @param {boolean} [passive] - Whether input is being selected passively. Default: `false`
   *
   * @see [`radio.init`]{@link module:core/form.radio.init}
   * @see [`Analytics.inputEvent`]{@link module:core/analytics.inputEvent}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input, deselect = false, passive = false) => {
    const optionActive = input.is('.active');

    // if we're deselecting an active input, just init the field
    // deselect can happen either if this is a facet input, or deselect is forced
    if (optionActive && deselect) radio.init(input);

    // if not active, set active class on desired input
    else if (!optionActive && !deselect) {
      // remove sibling active states
      input.parent('li').siblings('li').children('.input').removeClass('active');
      // add active state to current option
      input.prop('checked', true).addClass('active');
      // run input analytics
      Analytics.inputEvent(input, deselect, passive);
    }
  },
}

/**
 * Manage checkbox input elements. See content settings for [checkbox field types]{@link module:core/config._field_type_checkbox} for usage information.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const checkbox = {
  /**
   * Event listeners for checkbox input interaction.
   *
   * - Runs [input actions]{@link module:core/view.inputActions} on `.js-checkboxOption` input state change
   *
   * @memberOf module:core/form.checkbox
   *
   * @since 3.0.0
   *
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    View.container.on('change', '.js-checkboxOption', (event) => {
      View.inputActions($(event.currentTarget));
    });
  },

  /**
   * Initializes checkbox inputs to default (unselected) state.
   *
   * For each input, sets property `checked: false`, removes attributes `aria-disabled` and `disabled`, and removes class `active`.
   *
   * @memberOf module:core/form.checkbox
   *
   * @since 3.0.0
   *
   * @param {jQuery} [inputs] - Checkbox inputs to initialize. Default: `View.container.find('.js-checkboxOption')`
   *
   * @see Used by:
   * @see [**`checkbox.setState`**]{@link module:core/form.checkbox.setState}
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  init: (inputs = View.container.find('.js-checkboxOption')) => {
    inputs.prop('checked', false).removeAttr('aria-disabled disabled').removeClass('active');
  },

  /**
   * Manage checkbox states.
   *
   * If an option is active or the `deselect` param is enabled, the option will be reset with [`init`]{@link module:core/form.checkbox.init}.
   *
   * Otherwise, activate the input:
   * - Set property `checked: true` and add class `active`.
   * - If the checkbox option is `exclusive`, [deselect]{@link module:core/form.deselectInputs} any other [active checkboxes]{@link module:core/view.getActiveInputs} in the group.
   * - If there are any other `exclusive` checkboxes present in the group, [deselect]{@link module:core/form.deselectInputs} them.
   * - Run the [analytics input event]{@link module:core/analytics.inputEvent}.
   *
   * *Note that in most cases, setting the `checked` property here is not strictly required, as it will have already been set by the user interaction. However, in some cases an input is not selected by direct interaction, e.g. when setting active selections on view load, so the property is set here as well to ensure the input is checked.*
   *
   * @memberOf module:core/form.checkbox
   *
   * @since 3.0.0
   *
   * @param {jQuery} input - Checkbox input object.
   * @param {boolean} [deselect] - Force input deselection. Default: `false`
   * @param {boolean} [passive] - Whether input is being selected passively. Default: `false`
   *
   * @see [`checkbox.init`]{@link module:core/form.checkbox.init}
   * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`deselectInputs`]{@link module:core/form.deselectInputs}
   * @see [`Analytics.inputEvent`]{@link module:core/analytics.inputEvent}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input, deselect = false, passive = false) => {
    const optionActive = input.hasClass('active');

    // if active or deselect is specified, init input
    if (optionActive || deselect) checkbox.init(input);
    // otherwise check box
    else {
      const group = input.closest('[role="group"]');
      const exclusiveInput = group.find('[data-exclusive]');
      // if an exclusive input exists in group, do some additional checks
      if (exclusiveInput.length) {
        // if checkbox is exclusive, uncheck other checkboxes in group
        if (input.is('[data-exclusive]')) {
          let groupActiveInputs = View.getActiveInputs(group);
          deselectInputs(groupActiveInputs);
        }
        // if an exclusive checkbox is active elsewhere in the group, deactivate it
        else if (exclusiveInput.hasClass('active')) deselectInputs(exclusiveInput);
      }
      // activate checkbox
      input.prop('checked', true).addClass('active');
      // run input analytics
      Analytics.inputEvent(input, deselect, passive);
    }
  },
}

/**
 * Manage select input elements. See content settings for [select field types]{@link module:core/config._field_type_select} for usage information.
 *
 * This field utilizes a custom select-like element, and follows [Select-Only Combobox Example]{@link https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html} guidelines for structure and interaction.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const select = {
  /**
   * Event listeners for select input interaction.
   *
   * - [Expand select dropdown]{@link module:core/form.select.expand} on `.js-selectToggle` click.
   * - If input is not disabled, run [input actions]{@link module:core/view.inputActions} on `.js-selectOption` click, and [collapse dropdown]{@link module:core/form.select.collapse}.
   * - Runs [keydown events]{@link module:core/form.select.keydownEvents} on `.js-select` keydown.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @see [`select.expand`]{@link module:core/form.select.expand}
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see [`select.collapse`]{@link module:core/form.select.collapse}
   * @see [`select.keydownEvents`]{@link module:core/form.select.keydownEvents}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    // expand select
    View.container.on('click', '.js-selectToggle', (event) => {
      event.stopPropagation();
      const field = $(event.currentTarget).parents('.js-select');
      select.expand(field);
    });

    // select option interaction
    View.container.on('click', '.js-selectOption', (event) => {
      const input = $(event.currentTarget);
      // do nothing if disabled
      if (input.is('[aria-disabled="true"]')) return;
      // run input actions
      View.inputActions(input);
      // collapse field listbox
      select.collapse(input.closest('.js-select'));
    });

    // select keypress listeners
    View.container.on('keydown', '.js-select', (event) => {
      select.keydownEvents(event);
    });
  },

  /**
   * Initialize select states to default (unselected) state.
   *
   * Each provided select field's options are reset by removing attributes `aria-selected`, `aria-disabled`, and `disabled`, and removing class `active`. In addition, each select field's [labels are reset]{@link module:core/form.select.setLabel}.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} [fields] - Select fields to initialize. Default: `View.container.find('.js-select')`
   *
   * @see [`select.setLabel`]{@link module:core/form.select.setLabel}
   * @see Used by:
   * @see [**`select.setState`**]{@link module:core/form.select.setState}
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  init: (fields = View.container.find('.js-select')) => {
    fields.each((i, element) => {
      const field = $(element);
      const options = field.find('.input');
      // reset options
      options.removeClass('active').removeAttr('disabled aria-disabled aria-selected');
      // set label
      select.setLabel(field);
    });
  },

  /**
   * Manage select states.
   *
   * If the current input is active, and `deselect` param is enabled, the select field is reset with [`init`]{@link module:core/form.select.init}.
   *
   * If the input is not active:
   * - the attribute `aria-selected: true` is set and class `active` is added to the current input, and both are removed from any sibling inputs.
   * - [Select label is updated]{@link module:core/form.select.setLabel} with new selection label.
   * - [Analytics input event]{@link module:core/analytics.inputEvent} is run.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} input - Select option object (element with class `js-selectOption`).
   * @param {boolean} [deselect] - Force input deselection. Default: `false`
   * @param {boolean} [passive] - Whether input is being selected passively. Default: `false`
   *
   * @see [`select.init`]{@link module:core/form.select.init}
   * @see [`select.collapse`]{@link module:core/form.select.collapse}
   * @see [`select.setLabel`]{@link module:core/form.select.setLabel}
   * @see [`Analytics.inputEvent`]{@link module:core/analytics.inputEvent}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input, deselect = false, passive = false) => {
    const field = input.parents('.js-select');
    const optionActive = input.hasClass('active');

    // if we're deselecting an active input, just init the field
    if (optionActive && deselect) select.init(field);

    // if option is not active, activate it
    else if (!optionActive) {
      let inputs = input.parent().find('.input');
      inputs.removeClass('active').removeAttr('aria-selected');
      input.addClass('active').attr('aria-selected', true);
      input.blur();
      // set label state
      select.setLabel(field);
      // run input analytics
      Analytics.inputEvent(input, deselect, passive);
    }
  },

  /**
   * Set the active selection label. This label shows which option(s) are currently selected in the collapsed listbox.
   *
   * If multiple selections are made, a comma-separated list of items will be displayed.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} field - Select field object (element with class `js-select`).
   *
   * @see Used by:
   * @see [**`select.init`**]{@link module:core/form.select.init}
   * @see [**`select.setState`**]{@link module:core/form.select.setState}
   */
  setLabel: (field) => {
    const titlesContainer = field.find('.js-selectLabelSelected');

    // setup select state, if active selections
    const selections = View.getActiveInputs(field);
    if (selections.length) {
      // get text from all active selections, as comma-separated list
      let titles = selections.toArray().map(element => $(element).html().trim()).join(', ');
      // add titles to titles container
      titlesContainer.html(titles);
      field.addClass('active');
    }
    // otherwise revert to beginning state
    else {
      titlesContainer.html('').removeAttr('aria-label');
      field.removeClass('active');
    }
  },

  /**
   * Expand and focus select field listbox.
   *
   * - Calculate height of the listbox, and truncate if necessary, to prevent running outside of the app's visible area.
   * - Add class `expanded` to containing `.js-field`. This allows the field to receive elevated z-index compared to surrounding fields.
   * - Add attribute `aria-expanded: true` to toggle button `.js-selectToggle`.
   * - Set focus on listbox element `[role="listbox"]`.
   * - [Activate click catch]{@link module:core/view.toggleClickCatch} with modifier class `selectActive`.
   * - Run [analytics `toggle_click` event]{@link module:core/analytics.event}.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} field - Select field object (element with class `js-select`).
   *
   * @param {jQuery} option - Option to scroll to. If one is not provided, the current active input will be used, if one exists.
   *
   * @see [`View.toggleClickCatch`]{@link module:core/view.toggleClickCatch}
   * @see [`Analytics.event`]{@link module:core/analytics.event}
   * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`select.scrollToOption`]{@link module:core/form.select.scrollToOption}
   * @see Used by:
   * @see [**`select.addEventListeners`**]{@link module:core/form.select.addEventListeners}
   * @see [**`select.keydownEvents`**]{@link module:core/form.select.keydownEvents}
   */
  expand: (field, option) => {
    const fieldOuter = field.parent('.js-field');
    const selectToggle = field.children('.js-selectToggle');
    const selectOptions = field.children('.js-selectOptions');
    const listbox = selectOptions.children('[role="listbox"]');

    // calculate max height of listbox
    const definedMax = parseInt(selectOptions.css('max-height'));
    const contentsHeight = listbox.outerHeight();
    const optionsHeight = selectOptions.outerHeight();
    const usableSpace = View.container.outerHeight() - selectOptions.offset().top - 50;
    const max = Math.min(definedMax, contentsHeight, usableSpace) + optionsHeight;
    selectOptions.css('height', `${max}px`);

    // open dropdown
    fieldOuter.addClass('expanded');
    selectToggle.attr('aria-expanded', true);
    listbox.attr('aria-hidden', false).focus();
    View.toggleClickCatch(true, 'selectActive');

    // send analytics event
    Analytics.event('toggle_click');

    // if no option was provided, attempt to get active input
    if (!option) option = View.getActiveInputs(field);
    // scroll to option
    if (option.length) select.scrollToOption(option, 250);
  },

  /**
   * Collapse select listbox.
   *
   * - Remove inline height styles added during [expand]{@link module:core/form.select.expand}.
   * - [Unset highlighted items]{@link module:core/form.select.setHighlight} from keyboard navigation, if necessary.
   * - Remove class `expanded` from containing `.js-field`.
   * - Remove attribute `aria-expanded` from toggle button `.js-selectToggle`.
   * - Set focus on the select toggle button after closing, unless using `passive` param.
   * - [Deactivate click catch]{@link module:core/view.toggleClickCatch}.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} [field] - Select field object (element with class `js-select`). If no field is provided, any currently expanded field will be selected. Default: `View.container.find('.js-selectToggle[aria-expanded="true"]').closest('.js-select')`
   *
   * @param {boolean} [passive] - When enabled, field will not be focused after collapsing listbox. Default: `false`
   *
   * @see [`select.setHighlight`]{@link module:core/form.select.setHighlight}
   * @see [`View.toggleClickCatch`]{@link module:core/view.toggleClickCatch}
   * @see Used by:
   * @see [**`select.setState`**]{@link module:core/form.select.setState}
   * @see [**`select.keydownEvents`**]{@link module:core/form.select.keydownEvents}
   */
  collapse: (field = View.container.find('.js-selectToggle[aria-expanded="true"]').closest('.js-select'), passive = false) => {
    const selectOuter = field.closest('.js-field');
    const selectToggle = field.children('.js-selectToggle');
    const listbox = selectOuter.find('[role="listbox"]');
    // remove height styles added by expand
    field.find('.js-selectOptions').attr('style', '');
    // unset highlight
    select.setHighlight(field);
    // update attributes
    selectOuter.removeClass('expanded');
    selectToggle.removeAttr('aria-expanded');
    listbox.attr('aria-hidden', true);
    // set focus on field, if not passive mode
    if (!passive) selectToggle.focus();

    // deactivate click catch
    View.toggleClickCatch();
  },

  /**
   * Set the current "highlighted" or active item during keyboard navigation. This function can accept either a select field or select option element as a parameter.
   *
   * Active highlights are always unset, including removing attribute `aria-activedescendant` from the listbox and class `highlighted` from any highlighted options.
   *
   * If provided `item` is a select option element, it will be set as the new highlighted option. The listbox will have the option's `id` set as its `aria-activedescendant` attribute value, and the option have the class `highlighted` added.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} item - Select field or option to set/unset highlight on. If setting a highlight, provide an option element (`.js-selectOption`). If unsetting highlight from a field, provide a select field element (`.js-select`).
   *
   * @see Used by:
   * @see [**`select.collapse`**]{@link module:core/form.select.collapse}
   * @see [**`select.keydownEvents`**]{@link module:core/form.select.keydownEvents}
   */
  setHighlight: (item) => {
    const setOptionHighlight = item.is('.js-selectOption');
    const listbox = item.closest('.js-field').find('[role="listbox"]');
    const highlighted = listbox.find('.highlighted[role="option"]');

    // unset any existing highlights, if they exist
    if (highlighted.length) {
      listbox.removeAttr('aria-activedescendant');
      highlighted.removeClass('highlighted');
    }

    // if an option is provided, set new highlight on it
    if (setOptionHighlight) {
      // set activedescendant on listbox
      listbox.attr('aria-activedescendant', item.attr('id'));
      // set active state on list item
      item.addClass('highlighted');
    }
  },

  /**
   * Keep select item visible in listbox. Measures height of listbox and relative position of the provided option, and scrolls option into view if not fully visible.
   *
   * Scrolling can be delayed with the `delayScroll` param if necessary, is in the case where an expand animation is occurring, which could cause incorrect measuring of the listbox height.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {jQuery} option - Option element to focus.
   *
   * @param {number} [delayScroll] - Time (in ms) to delay scrolling to highlighted item. Time should be equal to any CSS listbox expanding animation.
   *
   * @see Used by:
   * @see [**`select.expand`**]{@link module:core/form.select.expand}
   * @see [**`select.keydownEvents`**]{@link module:core/form.select.keydownEvents}
   */
  scrollToOption: (option, delayScroll = 0) => {
    setTimeout(() => {
      const listboxOuterHeight = option.parents('.js-selectOptions').outerHeight();
      const inputHeight = option.outerHeight();
      const inputPosTop = option.position().top;
      const inputPosBottom = inputPosTop + inputHeight;
      const isAbove = inputPosTop < -1; // i.e. more than one whole pixel above
      const isBelow = inputPosBottom > listboxOuterHeight;
      if (isAbove) option[0].scrollIntoView();
      else if (isBelow) option[0].scrollIntoView(false);
    }, delayScroll);
  },

  /**
   * Keyboard control functionality for select input. Follows guidelines in W3's [Select-Only Combobox Example]{@link https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html} for how users should be able to interact with the select element.
   *
   * All keydown events on select fields are routed through this function, and any necessary actions are taken. Note that when navigating the select options, each individual option does not receive focus; rather the listbox retains focus and the currently "highlighted" item is described as an active descendant of the listbox.
   *
   * Actions to be taken on a give keypress depends on the state of the select field (either expanded or collapsed):
   *
   * #### Collapsed actions
   *
   * - `enter`, `space`, `down`, `up` - select will be [expanded]{@link module:core/form.select.expand}, and either the first item or any currently active selection will be [highlighted]{@link module:core/form.select.setHighlight} and receive "focus".
   *
   * #### Expanded actions
   *
   * - `enter`, `space`, `tab` - the currently highlighted item is [activated]{@link module:core/view.inputActions}, and the select is [collapsed]{@link module:core/form.select.collapse}. In the case of `tab`, the default action is also taken and the focus moves to the next focusable item.
   *
   * - `down`, `up` - the currently highlighted item will change to the next or previous item, respectively. The highlighted item will stop at the top or bottom of the list, i.e. it will not wrap around to the opposite end of the list.
   *
   * - `home`, `end` - the currently highlighted item will change to the first or last item, respectively.
   *
   * - `pageUp`, `pageDown` - jump to 10 inputs up or down, respectively. If there are not enough inputs to jump that far, jump to the first or last input.
   *
   * - `esc` - select is [collapsed]{@link module:core/form.select.collapse} with no change to the selected input.
   *
   * @memberOf module:core/form.select
   *
   * @since 3.0.0
   *
   * @param {object} event - jQuery event object.
   *
   * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`select.expand`]{@link module:core/form.select.expand}
   * @see [`select.setHighlight`]{@link module:core/form.select.setHighlight}
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see [`select.collapse`]{@link module:core/form.select.collapse}
   * @see [`select.scrollToOption`]{@link module:core/form.select.scrollToOption}
   * @see Used by:
   * @see [**`select.addEventListeners`**]{@link module:core/form.select.addEventListeners}
   */
  keydownEvents: (event) => {
    const field = $(event.currentTarget);
    const optionGroup = field.find('.js-optionGroup');
    const selectToggle = field.find('.js-selectToggle');
    const enabledInputs = optionGroup.find('.input').not('.hidden, [aria-disabled="true"]');
    const isExpanded = selectToggle.is('[aria-expanded="true"]');
    let active = View.getActiveInputs(field);
    let highlighted = optionGroup.find('.highlighted');
    let selection;

    // collapsed actions
    if (!isExpanded) {
      // each of these keys should open dropdown
      const expandKeys = ['enter', 'space', 'down', 'up'];
      // if event is not any of these keys, stop here
      if (!expandKeys.map(key => View.keycode[key]).includes(event.keyCode)) return;
      // prevent default actions
      event.preventDefault();
      // set selection to active or first input
      selection = active.length ? active : enabledInputs.first();
      // expand and highlight
      select.expand(field, selection);
      if (selection.length) select.setHighlight(selection);
    }

    // expanded actions
    else {
      selection = highlighted.length ? highlighted : active;
      // if there is no current selection, do nothing
      if (!selection.length) return;
      // assume we are not changing the highlight
      let newHighlight = false;
      // each of these keys should navigate in some way
      let navKeys = ['enter', 'space', 'down', 'up', 'home', 'end', 'tab', 'esc', 'pageUp', 'pageDown'];
      // if event does not contain any of them, stop here
      if (!navKeys.map(key => View.keycode[key]).includes(event.keyCode)) return;
      // prevent default actions, unless tab
      if (event.keyCode !== View.keycode.tab) event.preventDefault();

      // get current selection index
      let si = enabledInputs.index(selection);

      // handle nav cases
      switch (event.keyCode) {
        // esc - collapse listbox with no selection change
        case View.keycode.esc:
          select.collapse(field, true);
          break;
        // tab, enter / space bar - select current input
        case View.keycode.tab:
        case View.keycode.enter:
        case View.keycode.space:
          View.inputActions(selection);
          select.collapse(field);
          break;
        // up / down - navigate to next / previous item
        // uses the keycode values (38, 40) to calculate the direction to move the index
        case View.keycode.down:
        case View.keycode.up:
          if (si + event.keyCode - 39 > -1) newHighlight = enabledInputs.eq(si + event.keyCode - 39);
          break;
        // home - navigate to first enabled item
        case View.keycode.home:
          newHighlight = enabledInputs.first();
          break;
        // end - navigate to last enabled item
        case View.keycode.end:
          newHighlight = enabledInputs.last();
          break;
        case View.keycode.pageUp:
          newHighlight = (si - 2 > -1) ? enabledInputs.eq(si - 2) : enabledInputs.first();
          break;
        case View.keycode.pageDown:
          newHighlight = (si + 2 < enabledInputs.length) ? enabledInputs.eq(si + 2) : enabledInputs.last();
          break;
      }

      // highlight our new selected element, if it exists
      if (newHighlight.length) {
        select.setHighlight(newHighlight);
        select.scrollToOption(newHighlight);
      }
    }
  },
}

/**
 * Manage file input elements. See content settings for [file]{@link module:core/config._field_type_file} field types for usage information.
 *
 * @namespace
 *
 * @since 3.2.3
 *
 * @type {object}
 */
export const file = {
  /**
   * Event listeners for file input interactions.
   *
   * - [Run input actions]{@link module:core/view.inputActions} on `.js-fileInput` change.
   *
   * @memberOf module:core/form.file
   *
   * @since 3.2.3
   *
   * @see [`View.inputActions`]{@link module:core/view.inputActions}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    // set active state on file inputs
    View.container.on('change', '.js-fileInput', (event) => {
      const input = $(event.currentTarget);
      View.inputActions(input);
    });
  },

  /**
   * Set file input state. Input is considered "active" when a file has been added.
   *
   * @memberOf module:core/form.file
   *
   * @since 3.2.3
   *
   * @param {jQuery} input - File input object.
   *
   * @see [`View.getElementData`]{@link module:core/view.getElementData}
   * @see Used by:
   * @see [**`View.inputActions`**]{@link module:core/view.inputActions}
   */
  setState: (input) => {
    // get file
    const [file] = input.prop('files');
    // update image preview
    const field = input.closest('.js-field');
    const filePreview = field.find('.js-filePreview');
    const fallbackSrc = input.attr('data-fallback-src');
    const previewSrc = file || fallbackSrc || '#';
    filePreview.attr('src', Util.getFileURL(previewSrc));
    // input is active if a file is present
    input.toggleClass('active', !!file);
    // preview is active if a file has been added, or a fallback src is provided
    const active = !!fallbackSrc || !!file;
    filePreview.attr('aria-hidden', !active);

    // if no file, stop here
    if (!file) return;

    // update attributes values
    const inputData = View.getElementData(input[0]);
    // if no attributes are present, stop here
    if (!inputData.attributes) return;
    // update each attribute label
    for (let attribute of inputData.attributes) attribute.value = file;
  },
};

/**
 * Manage repeater field functionality. See content settings for [repeater]{@link module:core/config._field_type_repeater} field types for usage information.
 *
 * @namespace
 *
 * @since 3.4.0
 *
 * @type {object}
 */
export const repeater = {
  /**
   * Event listeners for repeater interactions.
   *
   * - [Remove repeater block]{@link module:core/form.repeater.removeBlock} on `.js-repeaterRemoveBlock` click.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @see [`repeater.removeBlock`]{@link module:core/form.repeater.removeBlock}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/form.addEventListeners}
   */
  addEventListeners: () => {
    // remove repeater block
    View.container.on('click', '.js-repeaterRemoveBlock', (event) => {
      let block = $(event.currentTarget).closest('.js-repeaterBlock');
      repeater.removeBlock(block);
    });
  },

  /**
   * Initialize repeater fields.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @see Used by:
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  init: () => {
    const fields = View.container.find('.js-repeater');
    // hide all blocks
    fields.find('.js-repeaterBlock').attr('aria-hidden', true).find('.js-field').addClass('hidden ignore');
    // set repeater states
    repeater.setState(fields);
  },

  /**
   * Set state of repeaters.
   *
   * For each repeater field:
   * - Determine if last block [has active fields]{@link module:core/view.getActiveInputs}. Based on this result, toggle `inactive` class on last block, and remove `ignore` class from any active inputs.
   * - Determine if any missing [required fields]{@link module:core/view.requiredFields} are present in visible blocks.
   * - If there are no blocks visible, or there are inputs active but no missing required, [add block]{@link module:core/form.repeater.addBlock}.
   * - [Set exclusive options states]{@link module:core/form.repeater.setExclusiveOptionsState}.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`View.requiredFields`]{@link module:core/view.requiredFields}
   * @see [`repeater.addBlock`]{@link module:core/form.repeater.addBlock}
   * @see [`repeater.setExclusiveOptionsState`]{@link module:core/form.repeater.setExclusiveOptionsState}
   * @see Used by:
   * @see [**`inputInit`**]{@link module:core/form.inputInit}
   */
  setState: (fields = View.container.find('.js-repeater')) => {
    fields.each((i, e) => {
      let field = $(e);
      let blocks = field.find('.js-repeaterBlock');
      let visible = blocks.filter('[aria-hidden="false"]');
      let last = visible.last();
      let lastActive = View.getActiveInputs(last);
      last.toggleClass('inactive', !lastActive.length);
      // if any inputs are active in the last block, remove ignore class from all fields
      if (lastActive.length) last.find('.js-field').removeClass('ignore');
      let missingRequired = View.requiredFields(visible);
      // if there are no blocks visible, or there are inputs active but no missing required, add block
      if (!visible.length || (lastActive.length && !missingRequired.length)) repeater.addBlock(field);
      // set state of any exclusive options
      repeater.setExclusiveOptionsState(blocks);
    });
  },

  /**
   * Handle repeater exclusive field options visibility.
   *
   * If any repeater exclusive fields are present (see [form field content settings]{@link module:core/config._field_type}), disable and hide any inputs which are selected in previous repeater blocks.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @param {jQuery} blocks - Repeater blocks to set options state in.
   *
   * @see [`enableInputs`]{@link module:core/form.enableInputs}
   * @see [`disableInputs`]{@link module:core/form.disableInputs}
   * @see Used by:
   * @see [**`setState`**]{@link module:core/form.repeater.setState}
   */
  setExclusiveOptionsState: (blocks) => {
    // handle repeater exclusive fields
    let activeInputIndices = [];
    blocks.each((i, e) => {
      const block = $(e);
      const exclusive = block.find('[data-repeater-exclusive]');
      exclusive.each((ii, ee) => {
        activeInputIndices[ii] = activeInputIndices[ii] || [];
        let field = $(ee);
        let inputs = field.find('.input');
        const available = inputs.filter((i, e) => !activeInputIndices[ii].includes($(e).index()));
        // get inputs to disable by index
        const unavailable = inputs.not(available);
        // disable/enable inputs
        enableInputs(available, true);
        disableInputs(unavailable, true);
        // add new active inputs to array
        let activeInputs = View.getActiveInputs(field);
        activeInputs.each((iii, eee) => {
          let index = $(eee).index();
          activeInputIndices[ii].push(index);
        });
      });
    });},

  /**
   * Add a new repeater block.
   *
   * Set attribute `aria-hidden="false"` and class `inactive` on first hidden block, and remove class `hidden` from any contained form fields.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @see Used by:
   * @see [**`repeater.setState`**]{@link module:core/form.repeater.setState}
   */
  addBlock: (field) => {
    let blocks = field.find('.js-repeaterBlock');
    let hidden = blocks.filter('[aria-hidden="true"]');
    // show first block and fields
    hidden.first().attr('aria-hidden', false).addClass('inactive').find('.js-field').removeClass('hidden');
  },

  /**
   * Remove repeater block.
   *
   * - [Deselect]{@link module:core/form.deselectInputs} any [active inputs]{@link module:core/view.getActiveInputs}
   * - Set attribute `aria-hidden="true"` on block, and classes `hidden ignore` on any contained form fields.
   * - Move "removed" block to the end of the repeater block list. This is so the repeater block order works as expected, e.g. all visible blocks are in order with no hidden blocks between them.
   * - [Update view state]{@link module:core/view.updateState}.
   *
   * @memberOf module:core/form.repeater
   *
   * @since 3.4.0
   *
   * @see [`deselectInputs`]{@link module:core/form.deselectInputs}
   * @see [`View.getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`View.updateState`]{@link module:core/view.updateState}
   * @see Used by:
   * @see [**`repeater.addEventListeners`**]{@link module:core/form.repeater.addEventListeners}
   */
  removeBlock: (block) => {
    // deselect any active block inputs
    deselectInputs(View.getActiveInputs(block), true);
    // hide block and fields
    block.attr('aria-hidden', true).find('.js-field').addClass('hidden ignore');
    // move removed block to end of list
    block.closest('.js-repeaterBlocks').append(block);
    // update view state
    View.updateState();
  },
};
